import { useEffect } from 'react';

const DEFAULT_NAVIGATION_SELECTORS = [
  '.bottom_navigation',
  '#single-workflow-navigation',
  '#mobile_bottom_submenu',
  '#mobile_submenu_overlay',
  '#navigation'
];

type HiddenElementState = {
  count: number;
  previousDisplay: string;
  previousDisplayPriority: string;
  previousAriaHidden: string | null;
  previousInert: boolean;
};

const hiddenElementsState = new WeakMap<HTMLElement, HiddenElementState>();
const HIDDEN_MOBILE_NAVIGATION_CLASS = 'mobile-navigation-hidden';
let hiddenMobileNavigationCount = 0;
let previousMobileNavigationHidden = false;

function getNavigationElements(selectors: string[]): HTMLElement[] {
  const elements = new Set<HTMLElement>();
  selectors.forEach(selector => {
    document.querySelectorAll<HTMLElement>(selector).forEach(element => {
      elements.add(element);
    });
  });
  return Array.from(elements);
}

function hideElement(element: HTMLElement): void {
  const current = hiddenElementsState.get(element);
  if (current) {
    hiddenElementsState.set(element, {
      ...current,
      count: current.count + 1
    });
    return;
  }

  hiddenElementsState.set(element, {
    count: 1,
    previousDisplay: element.style.display,
    previousDisplayPriority: element.style.getPropertyPriority('display'),
    previousAriaHidden: element.getAttribute('aria-hidden'),
    previousInert: element.inert === true
  });

  element.style.setProperty('display', 'none', 'important');
  element.setAttribute('aria-hidden', 'true');
  element.inert = true;
}

function restoreElement(element: HTMLElement): void {
  const current = hiddenElementsState.get(element);
  if (!current) {
    return;
  }

  if (current.count > 1) {
    hiddenElementsState.set(element, {
      ...current,
      count: current.count - 1
    });
    return;
  }

  element.style.removeProperty('display');
  if (current.previousDisplay) {
    element.style.setProperty(
      'display',
      current.previousDisplay,
      current.previousDisplayPriority
    );
  }
  if (current.previousAriaHidden === null) {
    element.removeAttribute('aria-hidden');
  } else {
    element.setAttribute('aria-hidden', current.previousAriaHidden);
  }
  element.inert = current.previousInert;
  hiddenElementsState.delete(element);
}

export function useHideNavigationWhileOpen(
  isOpen: boolean,
  selectors = DEFAULT_NAVIGATION_SELECTORS
): void {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const elements = getNavigationElements(selectors);
    elements.forEach(hideElement);
    const hidesMobileNavigation = elements.some(element =>
      element.matches('.bottom_navigation, #single-workflow-navigation')
    );
    const body = document.body;
    if (hidesMobileNavigation) {
      if (hiddenMobileNavigationCount === 0) {
        previousMobileNavigationHidden = body.classList.contains(
          HIDDEN_MOBILE_NAVIGATION_CLASS
        );
      }
      hiddenMobileNavigationCount++;
      body.classList.add(HIDDEN_MOBILE_NAVIGATION_CLASS);
    }

    return () => {
      elements.forEach(restoreElement);
      if (hidesMobileNavigation) {
        hiddenMobileNavigationCount--;
        if (
          hiddenMobileNavigationCount === 0 &&
          !previousMobileNavigationHidden
        ) {
          body.classList.remove(HIDDEN_MOBILE_NAVIGATION_CLASS);
        }
      }
    };
  }, [isOpen, selectors]);
}
